import React, { useEffect, useContext, useCallback, useState } from "react";
import { useSidebarState } from "react-admin";
import Grid from "@mui/material/Grid";
import Container from "@mui/material/Container";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DeleteIcon from "@mui/icons-material/Delete";
import { styled } from "@mui/material/styles";

import { useProfile } from "../DataHooks/useProfile";
import Player from "./Player";
import CommentList from "./CommentList";
// import ControlPanel from "./ControlPanel";
import SimpleTab from "./SimpleTab";
// import OrderTable from "./OrderTable";
import OrderTableNew from "./OrderTableNew";
import AddComment from "./AddComment";
import LiveCommentSubscriber from "./LiveCommentSubscriber";

import {
  store,
  SET_PAGE,
  ADD_VIDEO,
  SET_COMMENT_SCROLL,
  SET_ENABLE_CUSTOMER_INFO,
} from "./store";
import { store as commentStore, CLEAR_COMMENT } from "./commentStore";

import QuickAddModal from "./QuickAddModal";
import ReplyCommentModal from "./ReplyCommentModal";
import EditCommentModal from "./EditCommentModal";
import Loader from "../Loader";
import SalesItemTable from "./Charts/SalesItemTable";
import { useFbLiveVideo, useFBVideo } from "../DataHooks/useFbLiveVideo";
import { useFBProfile } from "../DataHooks/fbPageProfile";
import { Box, TextField } from "@mui/material";

// Styled components
const RedButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.error.main,
  color: theme.palette.getContrastText(theme.palette.error.main),
  "&:hover": {
    backgroundColor: theme.palette.error.dark,
  },
}));

const LiveWindow = () => {
  const [open, setOpen] = useSidebarState();
  const { profile } = useProfile();
  const fbLiveVideo = useFbLiveVideo();
  const { data: fbProfile } = useFBProfile();

  const { dispatch, state } = useContext(store);
  const { dispatch: commentDispatch } = useContext(commentStore);
  const [manualVideoLink, setManualVideoLink] = useState("");
  const [manualVideoId, setManualVideoId] = useState(null);
  const { data: fbVideo, isLoading: fbVideoLoading } = useFBVideo(
    manualVideoId && profile?.page?.psid ? manualVideoId : null,
    profile?.page?.psid
  );

  const {
    video,
    loading,
    commentScroll,
    quickCreateTarget,
    replyCommentTo,
    enableCustomerInfo,
    editComment,
  } = state;

  useEffect(() => {
    if (fbVideo) {
      dispatch({ type: ADD_VIDEO, payload: fbVideo });
      setOpen(false);
    } else if (fbLiveVideo) {
      dispatch({ type: ADD_VIDEO, payload: fbLiveVideo });
      setOpen(false);
    }
  }, [fbVideo, fbLiveVideo, dispatch, setOpen]);

  useEffect(() => {
    if (fbProfile) {
      dispatch({ type: SET_PAGE, payload: fbProfile });
    }
  }, [dispatch, fbProfile]);

  const onClearComment = useCallback(
    () => commentDispatch({ type: CLEAR_COMMENT }),
    [commentDispatch]
  );

  const onToggleCommentScroll = useCallback(() => {
    dispatch({ type: SET_COMMENT_SCROLL, payload: !commentScroll });
  }, [dispatch, commentScroll]);

  const onToggleCustomerInfo = useCallback(() => {
    dispatch({
      type: SET_ENABLE_CUSTOMER_INFO,
      payload: !enableCustomerInfo,
    });
  }, [dispatch, enableCustomerInfo]);

  const handleManualStart = () => {
    let videoId = null;

    try {
      const url = new URL(manualVideoLink.trim());

      // Case 1: https://www.facebook.com/watch/?v=XXXXXXXXX
      if (url.searchParams.has("v")) {
        videoId = url.searchParams.get("v");
      }

      // Case 2: https://www.facebook.com/{page}/videos/XXXXXXXXX
      if (!videoId) {
        const match = url.pathname.match(/\/videos\/(\d+)/);
        if (match) videoId = match[1];
      }

      // Case 3: fallback for pasted ID directly
      if (!videoId && /^\d+$/.test(manualVideoLink.trim())) {
        videoId = manualVideoLink.trim();
      }

      if (videoId) {
        setManualVideoId(videoId);
      } else {
        alert("Invalid Facebook video link");
      }
    } catch (e) {
      alert("Invalid link format");
    }
  };

  if (!video) {
    return (
      <Container sx={{ textAlign: "center", mt: 4 }}>
        <p>No Live video found. Go Live first, then refresh this page.</p>
        <TextField
          label="Enter Live Video Link"
          variant="outlined"
          size="small"
          fullWidth
          sx={{ maxWidth: 400, mt: 2 }}
          value={manualVideoLink}
          onChange={(e) => setManualVideoLink(e.target.value)}
        />
        <Button
          variant="contained"
          color="primary"
          sx={{ mt: 2 }}
          onClick={handleManualStart}
          disabled={!manualVideoLink.trim() || fbVideoLoading}
        >
          {fbVideoLoading ? "Loading..." : "Start"}
        </Button>
      </Container>
    );
  }

  return (
    <>
      <Box sx={{ width: "100vw", py: 2 }}>
        <LiveCommentSubscriber />

        <Grid container columns={20} spacing={3} sx={{ height: "100%" }}>
          {/* Left Column */}
          <Grid
            size={{ xs: 20, md: 10 }}
            display="flex"
            flexDirection="column"
            gap={2}
          >
            <OrderTableNew />
            <SalesItemTable />
            <SimpleTab />
          </Grid>

          {/* Right Column */}
          <Grid
            size={{ xs: 20, md: 9 }}
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "85vh", // total column height
              minHeight: 0,
            }}
          >
            <Box sx={{ flex: 9, minHeight: 0, display: "flex" }}>
              <CommentList style={{ flex: 1 }} />
            </Box>
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <ButtonGroup color="secondary">
                <RedButton
                  variant="contained"
                  startIcon={<DeleteIcon />}
                  onClick={onClearComment}
                >
                  Clear
                </RedButton>
                <Button
                  variant="contained"
                  startIcon={commentScroll ? <PauseIcon /> : <PlayArrowIcon />}
                  onClick={onToggleCommentScroll}
                >
                  {commentScroll ? "Pause" : "Play"}
                </Button>
                <Button variant="contained" onClick={onToggleCustomerInfo}>
                  {enableCustomerInfo ? "Hide info" : "Show info"}
                </Button>
              </ButtonGroup>

              <AddComment video={video} />
            </Box>
          </Grid>

          {/* Bottom Row */}
          <Grid size={{ xs: 20 }} display="flex" gap={2}>
            <Grid size={{ xs: 14 }}>{/* <SimpleTab /> */}</Grid>
            <Grid size={{ xs: 6 }}>
              <Player
                url={`https://www.facebook.com${video?.permalink_url || ""}`}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Modals & Loader */}
        {!!quickCreateTarget && <QuickAddModal />}
        {!!replyCommentTo && <ReplyCommentModal />}
        {!!editComment && <EditCommentModal />}
        <Loader open={loading} />
      </Box>
    </>
  );
};

export default LiveWindow;
